import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import Layout from "../../component/layout";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import {
  getBookingConfigURL,
  updateBookingConfigURL
} from "../../../../utils/urls";
import CancellationPoliciesTab from "./CancellationPoliciesTab";

const BookingSettings = () => {
  const [activeTab, setActiveTab] = useState("dayEnds");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dayEndTime, setDayEndTime] = useState("00:00");
  const [reminderMinutesBeforeDayEnd, setReminderMinutesBeforeDayEnd] = useState(60);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected", "error");
        return;
      }

      const response = await makeRequest2(`${getBookingConfigURL}/${facilityId}`, "GET");
      if (response.success && response.data) {
        const config = response.data.data || response.data;
        setDayEndTime(config.settings?.dayEndTime || "00:00");
        setReminderMinutesBeforeDayEnd(config.settings?.reminderMinutesBeforeDayEnd ?? 60);
      } else {
        toastify(response.error || "Failed to load booking settings", "error");
      }
    } catch (error) {
      console.error("Error fetching booking config:", error);
      toastify("Failed to load booking settings", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const facilityId = await getItem("selectedFacilityId");

      const response = await makeRequest2(
        `${updateBookingConfigURL}/${facilityId}`,
        "PUT",
        {
          dayEndTime,
          reminderMinutesBeforeDayEnd: Number(reminderMinutesBeforeDayEnd) || 0
        }
      );

      if (response.success) {
        toastify("Booking settings updated", "success");
      } else {
        toastify(response.error || "Failed to update booking settings", "error");
      }
    } catch (error) {
      console.error("Error updating booking config:", error);
      toastify(error.message || "Failed to update booking settings", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={"/facility/"}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={"/facility/booking_management"}>Booking Management</Link>
                </li>
                <li className="breadcrumb-item">Booking Settings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-body py-0">
              <ul className="nav nav-tabs profile-tabs" role="tablist">
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "dayEnds" ? "active" : ""}`}
                    onClick={() => setActiveTab("dayEnds")}
                    href="#day-ends"
                    role="tab"
                  >
                    Day Ends
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "cancellationPolicies" ? "active" : ""}`}
                    onClick={() => setActiveTab("cancellationPolicies")}
                    href="#cancellation-policies"
                    role="tab"
                  >
                    Cancellation Policies
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {activeTab === "cancellationPolicies" && <CancellationPoliciesTab />}

          {activeTab === "dayEnds" && (
            <div className="card">
              <div className="card-header">
                <h5>Day Ends</h5>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-4">
                  Day Ends sets the business-day cutoff used for check-in/checkout
                  night calculations - a guest arriving before this time counts
                  towards the previous day's booking.
                </p>

                <div className="row mb-4">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Day Ends At</label>
                    <input
                      type="time"
                      className="form-control"
                      value={dayEndTime}
                      onChange={(e) => setDayEndTime(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Per-Night Payment Reminder</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={reminderMinutesBeforeDayEnd}
                        onChange={(e) => setReminderMinutesBeforeDayEnd(e.target.value)}
                        disabled={loading}
                      />
                      <span className="input-group-text">min before Day Ends</span>
                    </div>
                    <small className="text-muted">
                      For "Per Night" bookings, guests are reminded to pay this many minutes before Day Ends At.
                    </small>
                  </div>
                </div>

                <div className="mt-4">
                  <Button
                    label={saving ? "Saving..." : "Save Settings"}
                    icon={saving ? "ti ti-loader animate-spin" : "ti ti-check"}
                    className="p-button-primary"
                    onClick={handleSave}
                    disabled={saving || loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BookingSettings;
